import React, { useEffect, useState } from 'react';

export default function VerifyTester() {
  const [supported, setSupported] = useState([]);
  const [gameId, setGameId] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageName, setImageName] = useState('');
  const [challengerScore, setChallengerScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [challengerHandle, setChallengerHandle] = useState('');
  const [opponentHandle, setOpponentHandle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/api/supported-games`)
      .then(r => r.json())
      .then(data => {
        setSupported(data.games || []);
        if ((data.games || []).length > 0) setGameId(data.games[0]);
      })
      .catch(() => setSupported([]));
  }, []);

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setImagePreview(dataUrl);
      // Strip data: prefix — backend handles either, but cleaner this way.
      const commaIdx = dataUrl.indexOf(',');
      setImageBase64(commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl);
    };
    reader.readAsDataURL(file);
  }

  async function onVerify() {
    setSubmitting(true);
    setResult(null);
    setError(null);
    try {
      const res = await fetch(`${process.env.PUBLIC_URL}/api/verify-screenshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gameId,
          imageBase64,
          claim: {
            challengerScore: Number(challengerScore),
            opponentScore: Number(opponentScore),
            challengerHandle: challengerHandle || undefined,
            opponentHandle: opponentHandle || undefined,
          },
        }),
      });
      const data = await res.json();
      setResult({ status: res.status, data });
    } catch (e) {
      setError(e.message || 'request failed');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = gameId && imageBase64 && !submitting;

  return (
    <div>
      <h2 style={{ color: 'var(--green)', marginTop: 0 }}>Verify screenshot</h2>
      <p style={{ color: 'var(--text-dim)', maxWidth: 640 }}>
        Send an image + claim to <code>/api/verify-screenshot</code>. Identical
        request shape to what the BLG service sends at score-submission time.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Field label="Game">
          <select value={gameId} onChange={e => setGameId(e.target.value)}>
            {supported.length === 0 && <option value="">(none — drop a template under templates/)</option>}
            {supported.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </Field>
        <Field label="Screenshot">
          <input type="file" accept="image/*" onChange={onFile} />
          {imageName && <small style={{ color: 'var(--text-dim)' }}>{imageName}</small>}
        </Field>
        <Field label="Challenger score">
          <input type="number" value={challengerScore} onChange={e => setChallengerScore(e.target.value)} />
        </Field>
        <Field label="Opponent score">
          <input type="number" value={opponentScore} onChange={e => setOpponentScore(e.target.value)} />
        </Field>
        <Field label="Challenger handle">
          <input type="text" value={challengerHandle} onChange={e => setChallengerHandle(e.target.value)} placeholder="e.g. ftw_kyle" />
        </Field>
        <Field label="Opponent handle">
          <input type="text" value={opponentHandle} onChange={e => setOpponentHandle(e.target.value)} placeholder="e.g. rivalRandy" />
        </Field>
      </div>

      {imagePreview && (
        <div style={{ marginTop: 16 }}>
          <img src={imagePreview} alt="preview" style={{ maxWidth: 320, borderRadius: 4, border: '1px solid var(--border)' }} />
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
        <button onClick={onVerify} disabled={!canSubmit}>
          {submitting ? 'Verifying…' : 'Verify'}
        </button>
        <button
          className="secondary"
          onClick={() => {
            setImageBase64('');
            setImagePreview(null);
            setImageName('');
            setResult(null);
            setError(null);
          }}
          disabled={submitting}
        >
          Clear
        </button>
      </div>

      {error && (
        <p style={{ color: 'var(--red)', marginTop: 16 }}>Error: {error}</p>
      )}
      {result && <ResultPanel result={result} />}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-dim)' }}>
      {label}
      {children}
    </label>
  );
}

function ResultPanel({ result }) {
  const data = result.data || {};
  const verdictColor = data.shouldReject
    ? 'var(--red)'
    : data.verdict === 'VALID'
    ? 'var(--green)'
    : 'var(--orange)';
  return (
    <div style={{ marginTop: 24, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 4, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: verdictColor }}>
          {data.verdict || '—'}
        </span>
        {data.confidence != null && (
          <span style={{ color: 'var(--text-dim)' }}>
            confidence {Number(data.confidence).toFixed(2)}
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-dim)', fontSize: 12 }}>
          HTTP {result.status}
        </span>
      </div>
      {data.shouldReject && data.rejectionReason && (
        <p style={{ color: 'var(--red)', marginTop: 8 }}>
          Would reject: {data.rejectionReason}
        </p>
      )}
      {data.reasoning && (
        <p style={{ color: 'var(--text)', marginTop: 8 }}>{data.reasoning}</p>
      )}
      {data.extracted && (
        <div style={{ marginTop: 12 }}>
          <strong style={{ fontSize: 12 }}>Extracted</strong>
          <pre>{JSON.stringify(data.extracted, null, 2)}</pre>
        </div>
      )}
      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--text-dim)', fontSize: 12 }}>
          Raw response
        </summary>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </details>
    </div>
  );
}
